import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import LoadingSpinner from '../components/LoadingSpinner'
import { referralApi, MyReferral, inviteLink } from '../api/referral'
import { userApi } from '../api/user'
import { isApp } from '../utils/platform'
import { toastUtil } from '../utils/toast'

/**
 * 친구 초대 (Figma `U7 내 추천 코드` 6360:2262, 2026-11-03 수정본 6413:7900).
 *
 * 내 추천인 코드 · 복사 · 공유 · 초대한 친구 수 · 연락처에서 친구 찾기 · 보상 자리.
 *
 * 추천인 코드는 **가입할 때만** 넣는다. 그래서 '초대한 친구'는 문자를 보낸 수가
 * 아니라 내 코드를 넣고 가입한 사람 수다(서버가 센다).
 *
 * '연락처에서 친구 찾기' — 휴대폰 기본 권한 팝업만 묻고 바로 연락처 친구 화면(초대 모드)으로
 * 간다. 온보딩의 연락처 동의 화면은 거치지 않는다(2026-11-04 도연님). 버튼을 누르고 권한을
 * 허용한 것을 동의로 보고 기록한다(updateContactConsent).
 *
 * 아래 버튼은 학교 인증 직후에 왔을 때만 있다('대학교 그룹으로 가기'). 설정에서 오면
 * 버튼 없이 왼쪽 위 뒤로가기로 나간다.
 */
const DOWNLOAD_URL = 'https://app.tastee.kr/download.html'
const BROWN = 'text-brown-80%-main'
const CARD = 'w-full bg-white border border-divider-divider-1'

const InviteFriends: React.FC = () => {
  const navigate = useNavigate()
  // 학교 인증 직후에 왔으면 뒤로 갈 곳이 없다(완료 화면은 기록에서 지웠다).
  // 뒤로가기를 숨기고, 아래 버튼이 학교 그룹으로 보낸다.
  const [searchParams] = useSearchParams()
  const afterUniversity = searchParams.get('from') === 'university'
  const [mine, setMine] = useState<MyReferral | null>(null)
  const [failed, setFailed] = useState(false)
  const [contactLoading, setContactLoading] = useState(false)

  const load = () => {
    setFailed(false)
    referralApi.getMine().then(setMine).catch(() => setFailed(true))
  }
  useEffect(load, [])

  // 연락처 권한·데이터 콜백. 화면을 떠나면 걷어 낸다(다른 화면의 콜백을 덮지 않게).
  /*
   * 허용됐는데 목록이 안 오는 경우를 끊는 타이머.
   * 앱(webview_contacts_handler)은 전화번호 있는 연락처가 0개면 '허용됨'만 보내고 목록은
   * 안 보낸다 — 아이폰에서 '일부 연락처만 허용'을 고르고 아무도 안 고른 경우가 그렇다.
   * 앱은 연락처를 6초 안에 읽으니, 7초 지나도 안 오면 없는 것으로 본다.
   */
  const waitRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stopWaiting = () => {
    if (waitRef.current) clearTimeout(waitRef.current)
    waitRef.current = null
  }

  useEffect(() => {
    const bridge = (window as any).FlutterBridge
    if (!isApp() || !bridge) return
    bridge.onContactsPermissionResult = (data: any) => {
      if (!data?.granted) {
        stopWaiting()
        setContactLoading(false)
        toastUtil.error('설정에서 연락처 접근을 허용해주세요')
        return
      }
      stopWaiting()
      waitRef.current = setTimeout(() => {
        waitRef.current = null
        setContactLoading(false)
        toastUtil.error('설정에서 연락처 접근을 허용해주세요')
      }, 7000)
    }
    bridge.onContactsData = (data: any) => {
      stopWaiting()
      setContactLoading(false)
      if (data && Array.isArray(data.contacts)) {
        userApi.updateContactConsent(true).catch(() => {})
        localStorage.setItem(
          'contacts_data',
          JSON.stringify({
            contacts: data.contacts,
            offset: data.offset ?? 0,
            total: data.total ?? data.contacts.length,
          })
        )
        navigate('/contact-friends?from=invite')
      }
    }
    return () => {
      stopWaiting()
      delete bridge.onContactsPermissionResult
      delete bridge.onContactsData
    }
  }, [navigate])

  const shareText = (code: string) =>
    `TASTEE에서 맛집 기록 같이 해요! 가입할 때 추천인 코드 ${code} 를 입력해 주세요 ${inviteLink(DOWNLOAD_URL, code)}`

  const copy = async () => {
    if (!mine) return
    try {
      await navigator.clipboard.writeText(mine.code)
      toastUtil.success('추천인 코드를 복사했어요')
    } catch {
      toastUtil.error('복사하지 못했어요')
    }
  }

  const share = async () => {
    if (!mine) return
    const text = shareText(mine.code)
    if (navigator.share) {
      try {
        await navigator.share({ title: 'TASTEE', text })
      } catch {
        /* 사용자가 닫음 */
      }
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      toastUtil.success('초대 문구를 복사했어요')
    } catch {
      toastUtil.error('공유하지 못했어요')
    }
  }

  const findFromContacts = () => {
    const bridge = (window as any).FlutterBridge
    if (!isApp() || !bridge) {
      toastUtil.error('연락처는 앱에서만 불러올 수 있어요')
      return
    }
    setContactLoading(true)
    bridge.requestContactsPermission()
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        <AppBar title="친구 초대" isBottomBorder showBack={!afterUniversity} />

        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pt-24px">
          {failed ? (
            <div className="py-80px flex flex-col items-center gap-12px">
              <p className="text-style-body1 text-textColorText3">잠시 후 다시 시도해주세요</p>
              <button type="button" onClick={load} className="text-style-button2 text-textColorText2 underline">
                다시 불러오기
              </button>
            </div>
          ) : !mine ? (
            <div className="py-80px flex justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/*
                2026-11-04 다시 그린 판(Figma `인증 직후 초대 화면` 6444:13842).
                버튼 아이콘을 빼고, 보상 자리를 카드 바로 아래로 올리고,
                초대한 친구·연락처 줄은 카드 없이 글자 줄로 둔다.
              */}
              <div className={`${CARD} rounded-20px flex flex-col items-center p-20px`}>
                <p className="font-pretendard font-bold text-16px leading-140% tracking-[-0.16px] text-[rgba(71,36,16,0.8)]">
                  내 추천인 코드
                </p>
                <p className={`mt-12px font-pretendard font-bold text-32px leading-130% tracking-[1.28px] ${BROWN}`}>
                  {mine.code}
                </p>
                <div className="mt-18px w-full flex gap-10px">
                  {[
                    { label: '복사하기', onClick: copy },
                    { label: '공유하기', onClick: share },
                  ].map((b) => (
                    <button
                      key={b.label}
                      type="button"
                      onClick={b.onClick}
                      className="flex-1 h-46px rounded-full bg-472410-10% flex items-center justify-center active:opacity-70"
                    >
                      <span className={`font-pretendard font-bold text-15px leading-140% tracking-[-0.15px] ${BROWN}`}>
                        {b.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/*
                초대한 친구 카드 (Figma 6447:14058, 2026-11-04).
                동그라미 세 칸(56px) — 내 코드로 가입한 최근 3명의 사진을 채우고(누르면 프로필), 빈 칸은 '+'.
                빈 칸을 누르면 공유하기와 같다. 3명보다 많으면 세 번째 칸이 '+남은 수'.
                닉네임은 안 보인다 — 누르면 프로필로 가니까(2026-11-04 도연님). 리워드 줄과 연락처 찾기도 이 카드 안에 둔다.
              */}
              <div className={`${CARD} mt-16px rounded-20px flex flex-col items-center p-20px`}>
                <p className="font-pretendard font-bold text-16px leading-140% tracking-[-0.16px] text-brown-80%-main">
                  초대한 친구 {mine.invitedCount.toLocaleString()}명
                </p>
                <div className="mt-12px flex gap-28px">
                  {[0, 1, 2].map((i) => {
                    // 3명보다 많으면 세 번째 칸은 남은 인원(+N). 사진은 최근 2명만.
                    const more = mine.invitedCount > 3 && i === 2 ? mine.invitedCount - 2 : 0
                    const f = more ? undefined : mine.invited?.[i]
                    return (
                      <div key={i} className="w-56px flex flex-col items-center">
                        {more ? (
                          <div className="w-56px h-56px rounded-full bg-[#f3eee8] flex items-center justify-center font-pretendard font-bold text-16px leading-none text-[#6f5e4d]">
                            +{more.toLocaleString()}
                          </div>
                        ) : f ? (
                          // 누르면 그 친구 프로필로 (리더보드에서 다른 사람을 누를 때와 같은 주소)
                          <button
                            type="button"
                            aria-label={`${f.nickname ?? '친구'} 프로필`}
                            onClick={() => navigate(`/user/${f.userId}/profile`)}
                            className="active:opacity-70"
                          >
                            <img
                              src={f.profileImage || '/images/profile-placeholder-icon.png'}
                              alt=""
                              className="w-56px h-56px rounded-full object-cover bg-[#f8f3ec]"
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            aria-label="친구 초대하기"
                            onClick={share}
                            className="w-56px h-56px rounded-full bg-[#f8f3ec] border-[1.5px] border-dashed border-[#c9bba9] flex items-center justify-center font-pretendard font-bold text-20px leading-none text-[#c9bba9] active:opacity-70"
                          >
                            +
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* 보상 자리 — 보상은 아직 미정 */}
                <div className="mt-16px w-full rounded-10px bg-[#f3eee8] flex items-center gap-6px px-10px py-9px">
                  <img src="/images/invite/gift-icon.svg" alt="" className="w-16px h-16px" />
                  <p className="font-pretendard font-medium text-14px leading-140% text-[#6f5e4d]">
                    친구 초대 리워드 준비 중이에요
                  </p>
                </div>

                {/* 연락처에서 친구 찾기 */}
                <button
                  type="button"
                  onClick={findFromContacts}
                  disabled={contactLoading}
                  className="mt-18px w-full flex items-center gap-12px pt-2px pb-4px text-left active:opacity-70 disabled:opacity-60"
                >
                  <p className="flex-1 font-pretendard font-bold text-14px leading-140% tracking-[-0.14px] text-brown-80%-main">
                    연락처에서 친구 찾기
                  </p>
                  {contactLoading ? (
                    <LoadingSpinner size={16} />
                  ) : (
                    <img src="/images/invite/chevron-right-icon.svg" alt="" className="w-16px h-16px" />
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {afterUniversity && (
          <div className="w-full px-20px">
            <SubmitButton
              text="대학교 그룹으로 가기"
              onSubmit={() => navigate('/university/group', { replace: true })}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default InviteFriends
